import { useFormik } from 'formik'
import { apiKeyValidation } from 'utils/validationsSchema'
import { ToastContext } from 'contexts'
import { useContext, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useUpdateApiKeyService } from 'services/apiKey/useUpdateApiKeyService'
import { useNavigate, useParams } from 'react-router-dom'
import {
    useGetAccountSubnetApiServices,
    useUpdateSubnetApiKey,
} from 'services/subnetApiService/useAccountSubnetApiService'
import { useGetSubnetApiServices } from 'services/subnetApiService/useSubnetApiService'
import { useApiKeysService } from 'services'

const useEditApiKey = ({
    data,
    callback,
    isSubnet,
}: {
    data: any
    callback?: () => void
    isSubnet?: boolean
}) => {
    //todo: update cache instead of refetch
    const { refetch: apiKeyRefetch } = useApiKeysService()

    const { t } = useTranslation()

    const navigate = useNavigate()
    const params = useParams()
    const { setToast } = useContext(ToastContext)
    const [updateApiKeyById] = useUpdateApiKeyService()
    const [isLoading, setIsLoading] = useState(false)

    const { updateSubnetApiKey } = useUpdateSubnetApiKey()
    const { data: account_subnets, refetch: accountSubnetRefetch } =
        useGetAccountSubnetApiServices()
    const { data: subnet_api_services, refetch: subnetApiRefetch } = useGetSubnetApiServices(
        params?.id || '',
    )

    const initial_filtered_account_subnets = account_subnets?.filter(
        (subnet: any) => subnet.api_key_id === data.id,
    )

    const matchingItems = subnet_api_services?.filter(subnetItem =>
        initial_filtered_account_subnets?.some(
            (account_subnet: any) => account_subnet.subnet_api_service_id === subnetItem.id,
        ),
    )
    const subnetAPiValues = matchingItems?.map(item => item.id)

    const defaultValues = {
        name: data.name,
        description: data.description,
        account_subnet_ids: subnetAPiValues,
    }

    const handleSubmit = async (values: {
        name: string
        description: string
        account_subnet_ids: string[]
    }) => {
        setIsLoading(true)
        try {
            const newValues = {
                name: values.name,
                description: values.description,
            }

            if (data.id) {
                await updateApiKeyById(data.id, { ...newValues })
                if (isSubnet) {
                    const filtered_account_subnets = account_subnets?.filter((subnet: any) =>
                        values.account_subnet_ids?.some(
                            (account_subnet_id: string) =>
                                account_subnet_id === subnet.subnet_api_service_id,
                        ),
                    )

                    const missingSubnets = initial_filtered_account_subnets?.filter(
                        (initialSubnet: any) =>
                            !filtered_account_subnets?.some(
                                (filteredSubnet: any) =>
                                    filteredSubnet.subnet_api_service_id ===
                                    initialSubnet.subnet_api_service_id,
                            ),
                    )

                    await Promise.all([
                        ...(missingSubnets ?? []).map(async (subnet: any) => {
                            await updateSubnetApiKey(subnet.id, { api_key_id: null })
                        }),
                        ...(filtered_account_subnets ?? []).map(async (subnet: any) => {
                            await updateSubnetApiKey(subnet.id, { api_key_id: data.id })
                        }),
                    ])

                    accountSubnetRefetch()
                    subnetApiRefetch()
                }
            }

            setToast({
                message: t('API key successfully updated'),
                type: 'positive',
                open: true,
            })
            apiKeyRefetch()
            if (callback) {
                callback()
            } else {
                navigate('/api-key')
            }
        } catch (error) {
            setToast({
                message: t('failed to updated API key'),
                type: 'negative',
                open: true,
            })
        }
        setIsLoading(false)
    }

    const formik = useFormik({
        initialValues: defaultValues,
        enableReinitialize: true,
        validationSchema: apiKeyValidation,
        onSubmit: async values => handleSubmit(values),
    })

    return {
        formik,
        isLoading,
        handleSubmit,
    }
}

export default useEditApiKey
